import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { signInWithCustomToken, signOut } from "firebase/auth";
import app, { defaultAuth, getCloudFunctionUrl } from "../../firebase";
import { useSession } from "../../context/SessionContext";
import {
  clearEnrolledSession,
  readEnrolledSession,
  subscribeRoleEvents,
  writeEnrolledSession,
  writeEnrolledStudentData
} from "../../auth/roleSession";

const db = getFirestore(app);
const ENROLLED_SIGN_IN_URL = getCloudFunctionUrl("enrolledStudentSignIn");

export default function SignInPage() {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useSession();

  const [studentId, setStudentId] = useState("");
  const [studentpassword, setStudentpassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [checkingSession, setCheckingSession] = useState(true);

  // Auto-redirect when user is already logged in via enrolled portal
  // Wait for auth to finish loading before checking
  useEffect(() => {
    const checkEnrolledSession = async () => {
      if (authLoading) return;
      setCheckingSession(true);
      {
        const enrolledSession = readEnrolledSession();
        const isEnrolledLoggedIn = !!enrolledSession?.studentId;
        const hasEnrolledSession = !!enrolledSession?.studentId;
        const isStudentLoggedIn = sessionStorage.getItem("studentPortalLogin") === "true";
        
        // ONLY redirect if explicitly logged in via enrolled portal AND has session
        // Do NOT redirect if student portal is logged in
        if (isEnrolledLoggedIn && hasEnrolledSession && !isStudentLoggedIn) {
          const userId = enrolledSession.studentId;
          
          // Verify student still exists in Firebase
          try {
            const studentDoc = await getDoc(doc(db, "students", userId));
            
            if (!studentDoc.exists()) {
              // Student was deleted from Firebase - logout
              console.log("[EnrolledPortal] Student deleted from Firebase, logging out");
              clearEnrolledSession();
              setMessage("Your account has been removed. Please contact the administrator.");
              setCheckingSession(false);
              return;
            }
          } catch (err) {
            console.error("[EnrolledPortal] Error checking student:", err);
            clearEnrolledSession();
            setCheckingSession(false);
            return;
          }
          
          console.log("[EnrolledPortal] Auto-redirecting with enrolledPortalLogin flag");
          navigate("/enrolled/dashboard", { replace: true });
        }
        setCheckingSession(false);
      }
    };
    
    checkEnrolledSession();
    return subscribeRoleEvents("Enrolled Student", () => checkEnrolledSession());
  }, [currentUser, navigate, authLoading]);

  if (checkingSession) return null;

  async function handleSubmit(e) {
    e?.preventDefault();
    setMessage("");
    if (!studentId.trim() || !studentpassword) {
      setMessage("Enter student ID and password.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(ENROLLED_SIGN_IN_URL, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({studentId: studentId.trim(), password: studentpassword})
      });
      const result = await response.json().catch(() => null);
      if (!response.ok || typeof result?.customToken !== "string" || !result.studentDocumentId) {
        setMessage(result?.error?.message || "Invalid student ID or password.");
        return;
      }
      const credential = await signInWithCustomToken(defaultAuth, result.customToken);
      const studentSnapshot = await getDoc(doc(db, "students", result.studentDocumentId));
      if (!studentSnapshot.exists() || studentSnapshot.data().uid !== credential.user.uid) {
        throw new Error("The student profile could not be verified.");
      }
      const uid = result.studentDocumentId;
      const studentData = studentSnapshot.data();
      const activeSession = readEnrolledSession();
      if (activeSession?.studentId && activeSession.studentId !== uid) {
        setMessage("Another enrolled student account is already active. Log out that account before signing in.");
        await signOut(defaultAuth);
        return;
      }
      writeEnrolledSession(uid, credential.user.uid);
      writeEnrolledStudentData({ id: uid, ...studentData });
      navigate("/enrolled/dashboard");
    } catch (err) {
      console.error("[EnrolledPortal] Login error:", err);
      setMessage(err?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "linear-gradient(90deg, #00b37a 0%, #0f5f3b 100%)"
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: 420,
          maxWidth: "100%",
          background: "#fff",
          padding: 28,
          borderRadius: 10,
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)"
        }}
      >
        <h2 style={{ marginTop: 0, color: "#0f5f3b", marginBottom: 18 }}>EnrollEase</h2>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 13, color: "#444", marginBottom: 6 }}>Student ID</label>
          <input
            type="text"
            value={studentId}
            onChange={(e) => setStudentId(e.target.value)}
            placeholder="Enter your student ID"
            style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #ddd", fontSize: 14 }}
            required
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={{ display: "block", fontSize: 13, color: "#444", marginBottom: 6 }}>Password</label>
          <input
            type={showPassword ? "text" : "password"}
            value={studentpassword}
            onChange={(e) => setStudentpassword(e.target.value)}
            placeholder="Enter password"
            style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #ddd", fontSize: 14 }}
            required
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start", marginBottom: 14 }}>
          <label style={{ fontSize: 13, color: "#444", display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={showPassword}
              onChange={() => setShowPassword((s) => !s)}
              style={{ width: 16, height: 16 }}
            />
            Show password
          </label>
        </div>

        {message && <div style={{ color: "#b00020", marginBottom: 12 }}>{message}</div>}

        <button
          type="submit"
          disabled={loading}
          style={{ width: "100%", padding: 12, background: loading ? "#ccc" : "#0f5f3b", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, cursor: loading ? "not-allowed" : "pointer" }}
        >
          {loading ? "Signing in..." : "Sign in"}
        </button>
        <div style={{ textAlign: "center", marginTop: 14 }}>
          <Link to="/" style={{ color: "#0f5f3b", fontSize: 14 }}>Return to home</Link>
        </div>
      </form>
    </div>
  );
}